import { useState } from "react";
import { useDropzone } from "react-dropzone";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/system";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { uploadProjects } from "../../api/uploadProjects";
import { getGroupByIdSimple } from "../../api/getGroupById";
import { FINAL_DOCUMENTS, missingFinalDocuments } from "../../utils/finalDocuments";
import { formatDate } from "../../utils/getEntitiesUtils";
import { TitleSimple } from "../../styles/Titles";
import { ButtonSimple } from "../Root";

// El mismo limite que UploadFile.js: 95 MiB, por debajo del techo de Caddy.
// Ver el comentario de alla y backend/src/config/config.py.
const MAX_BYTES = 95 * 1024 * 1024;
const EXTERNAL_FORM = "https://forms.gle/8Hg5StAkDDD3xfxp9";

const DropzoneBox = styled(Box)(({ theme }) => ({
  border: "2px dashed #cccccc",
  padding: theme.spacing(2),
  textAlign: "center",
  cursor: "pointer",
  color: "rgba(0, 0, 0, 0.6)",
  minHeight: "90px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const DocumentDropzone = ({ document, uploadedAt, file, error, onSelect }) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles) => onSelect(document.key, acceptedFiles[0]),
    accept: "application/pdf",
    multiple: false,
  });

  return (
    <Box sx={{ marginBottom: 2 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle1" fontWeight="bold">
          {document.label}
        </Typography>
        <Typography variant="body2" color={uploadedAt ? "success.main" : "text.secondary"}>
          {uploadedAt ? `Subido el ${formatDate(uploadedAt)}` : "Falta"}
        </Typography>
      </Box>
      <DropzoneBox {...getRootProps()}>
        <input {...getInputProps()} />
        {file ? (
          <p>Archivo seleccionado: {file.name}</p>
        ) : isDragActive ? (
          <p>Suelta el archivo aquí...</p>
        ) : (
          <p>
            {uploadedAt
              ? "Arrastra un PDF aquí, o haz clic, para reemplazarlo"
              : "Arrastra el PDF aquí, o haz clic para seleccionarlo"}
          </p>
        )}
      </DropzoneBox>
      {error && (
        <Typography variant="body2" color="error">
          {error}{" "}
          <a href={EXTERNAL_FORM} target="_blank" rel="noopener noreferrer">
            {EXTERNAL_FORM}
          </a>
        </Typography>
      )}
    </Box>
  );
};

// Entrega final: tres PDFs obligatorios, cada uno en su zona. Se puede subir
// cualquier subconjunto y reemplazar uno sin tocar los otros; la entrega queda
// completa cuando el backend tiene los tres.
const UploadFinalDocuments = ({ group: initialGroup }) => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);
  // El id sale del usuario y no del grupo: GET /groups/states/{id} devuelve
  // solo los estados, sin el id. Es lo mismo que hace UploadFile.js.
  const groupId = useSelector((state) => state.user.group_id);
  const [group, setGroup] = useState(initialGroup);
  const [projectTitle, setProjectTitle] = useState(initialGroup?.final_report_title || "");
  const [titleError, setTitleError] = useState("");
  const [files, setFiles] = useState({});
  const [fileErrors, setFileErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const missing = missingFinalDocuments(group);
  const complete = missing.length === 0;

  const selectFile = (key, file) => {
    if (file && file.name.toLowerCase().endsWith(".pdf") && file.size <= MAX_BYTES) {
      setFiles({ ...files, [key]: file });
      setFileErrors({ ...fileErrors, [key]: "" });
    } else {
      setFiles({ ...files, [key]: null });
      setFileErrors({
        ...fileErrors,
        [key]: "Por favor cargue un archivo PDF que no supere los 95MB. O envialo a través del siguiente link:",
      });
    }
    setFormError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!projectTitle.trim()) {
      setTitleError("Por favor ingrese el título del proyecto.");
      return;
    }
    if (projectTitle.length > 95) {
      setTitleError("El título no puede exceder los 95 caracteres.");
      return;
    }
    setTitleError("");

    const selected = FINAL_DOCUMENTS.filter((d) => files[d.key]);
    if (selected.length === 0) {
      setFormError("Seleccioná al menos un documento para subir.");
      return;
    }
    setFormError("");
    setLoading(true);

    // De a uno: cada subida es una tarea de fondo en el backend y esperarlas en
    // orden deja un resultado claro por documento.
    const outcome = [];
    for (const document of selected) {
      const { success, message } = await uploadProjects({
        projectType: "final-project",
        subpath: "groups",
        id: groupId,
        projectTitle,
        selectedFile: files[document.key],
        token: user.token,
        document: document.key,
        documentLabel: document.label,
      });
      outcome.push({ document, success, message });
    }

    let updated = group;
    try {
      updated = await getGroupByIdSimple(user, groupId);
      setGroup(updated);
    } catch (error) {
      console.error("Error al refrescar el equipo:", error);
    }
    setFiles(
      Object.fromEntries(
        outcome.filter((r) => !r.success).map((r) => [r.document.key, files[r.document.key]])
      )
    );
    setResults({ outcome, complete: missingFinalDocuments(updated).length === 0 });
    setLoading(false);
  };

  const handleCloseDialog = () => {
    const done = results?.complete && results.outcome.every((r) => r.success);
    setResults(null);
    if (done) {
      navigate("/learning-path");
    }
  };

  const allOk = results?.outcome.every((r) => r.success);
  const stillMissing = missingFinalDocuments(group);

  return (
    <div>
      <Box textAlign="center">
        <TitleSimple variant="h5">Subir Entrega Final</TitleSimple>
      </Box>

      <Alert severity={complete ? "success" : "info"} sx={{ marginBottom: 2 }}>
        {complete
          ? `Tu equipo completó esta entrega el ${formatDate(group.final_report_date)}. Podés reemplazar cualquiera de los documentos.`
          : `La entrega final tiene tres documentos obligatorios. Falta${missing.length > 1 ? "n" : ""}: ${missing
              .map((d) => d.label)
              .join(", ")}.`}
      </Alert>

      <form onSubmit={handleSubmit}>
        <TextField
          label="Ingrese el título del proyecto"
          variant="outlined"
          fullWidth
          margin="normal"
          value={projectTitle}
          onChange={(e) => setProjectTitle(e.target.value)}
          error={Boolean(titleError)}
          helperText={titleError}
        />

        {FINAL_DOCUMENTS.map((document) => (
          <DocumentDropzone
            key={document.key}
            document={document}
            uploadedAt={group?.[document.dateField]}
            file={files[document.key]}
            error={fileErrors[document.key]}
            onSelect={selectFile}
          />
        ))}

        {formError && (
          <Typography variant="body2" color="error">
            {formError}
          </Typography>
        )}

        <ButtonSimple variant="contained" color="primary" type="submit" fullWidth disabled={loading}>
          {loading ? "Cargando..." : "Aceptar"}
        </ButtonSimple>
      </form>

      <Dialog open={Boolean(results)} onClose={handleCloseDialog} maxWidth="xs" fullWidth>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "24px 16px" }}
        >
          {allOk ? (
            <CheckCircleIcon sx={{ fontSize: 60, color: "#4CAF50" }} />
          ) : (
            <ErrorIcon sx={{ fontSize: 60, color: "#F44336" }} />
          )}
          {results?.outcome.map((r) => (
            <Typography
              key={r.document.key}
              variant="body2"
              align="center"
              sx={{ marginTop: "8px", color: r.success ? "text.secondary" : "#F44336" }}
            >
              {r.message}
            </Typography>
          ))}
          {results && (
            <Typography variant="body2" align="center" sx={{ marginTop: "16px", fontWeight: 600 }}>
              {results.complete
                ? "La entrega final está completa."
                : `La entrega todavía no está completa. Falta${stillMissing.length > 1 ? "n" : ""}: ${stillMissing
                    .map((d) => d.label)
                    .join(", ")}.`}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", paddingBottom: "16px" }}>
          <Button onClick={handleCloseDialog} variant="contained">
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default UploadFinalDocuments;
